import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { useGroupsUIStore } from '../../groups-layer/stores/groups.store'
import { refreshMemberOnboardingAfterActivity } from '../../welcome-onboarding-layer/utils/welcome-onboarding-activity-refresh.utils'
import { EventsService } from '../services/events.service'

import type { EventFormDataType, MeetingType, EventMentionData, EventSearchResult } from '../types/events'

/**
 * Events Query Store - Handles fetching events data using TanStack Query
 * Focused on searching events for mentions
 */
export const useEventsQueryStore = (groupId: MaybeRefOrGetter<string | undefined>) => {
  const service = new EventsService()
  const groupsStore = useGroupsUIStore()
  const queryClient = useQueryClient()

  // ===== QUERY KEY FACTORY =====
  const queryKeys = {
    all: (gId?: string) => ['events', gId] as const,
    list: (gId?: string) => ['events', gId, 'list'] as const,
    search: (gId?: string, search?: string) => ['events', gId, 'search', search] as const,
    calendar: (gId?: string, startDate?: string, endDate?: string) => ['events', gId, 'calendar', startDate, endDate] as const,
    incoming: (gId?: string) => ['events', gId, 'incoming'] as const,
    count: (gId?: string, eventId?: string) => ['events', gId, 'count', eventId] as const,
    attendees: (gId?: string, eventId?: string) => ['events', gId, 'attendees', eventId] as const,
    exportAttendees: (gId?: string, eventId?: string) => ['events', gId, 'export-attendees', eventId] as const,
    // BE resolves the next occurrence server-side; one cache entry per eventId.
    detail: (gId?: string, locId?: string, eventId?: string) => ['events', gId, 'detail', locId, eventId] as const,
  }

  // ===== SEARCH EVENTS FOR MENTIONS QUERY =====
  const searchEventsQuery = (searchText: MaybeRefOrGetter<string>) =>
    useQuery<EventMentionData[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const search = toValue(searchText)
        return queryKeys.search(gId, search)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        const search = toValue(searchText)

        if (!gId || !locationId) {
          throw new Error('Group ID and Location ID are required for searching events')
        }

        // Set date range: 12 months ahead and 2 months back
        const now = new Date()
        const startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1)
        const endDate = new Date(now.getFullYear(), now.getMonth() + 12, 0)

        const events = await service.searchTaggableEvents({
          search,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupId: gId,
          locationId,
        })

        // Transform to mention format
        return events.slice(0, 5).map(event => {
          const occurrenceStartTime = event.isRecurring ? event.nextOccurrence || event.startTime : event.startTime
          // Save timestamp in milliseconds to reduce stored string size in db
          const eventDate = new Date(occurrenceStartTime as string).getTime()

          return {
            id: event._id,
            label: event.title,
            type: 'events-mention' as const,
            timestamp: eventDate,
            startTime: occurrenceStartTime,
            endTime: event.endTime,
            nextOccurrence: event.nextOccurrence,
            isRecurring: event.isRecurring,
            eventCoverUrl: event.eventCoverUrl,
            description: event.description,
          }
        })
      },
      staleTime: 30 * 1000, // 30 seconds for search results
      gcTime: 60 * 1000, // 1 minute
      enabled: computed(() => {
        const gId = toValue(groupId)
        const search = toValue(searchText)
        return !!gId && !!search && search.length > 0
      }),
      refetchOnWindowFocus: false,
    })

  // ===== SINGLE EVENT BY ID QUERY =====
  /**
   * Fetch a single event by id. Backend resolves the next recurring occurrence
   * server-side (`nextOccurrence` + shifted `startTime`/`endTime`).
   * Used by event mention popovers and inline embed cards — multiple consumers
   * for the same eventId share one in-flight request and one cache entry.
   *
   * @param eventId - the event id (e.g. from `data-id` on `<a data-type="events-mention">`)
   * @param options - `enabled` to gate auto-fetch (e.g. for visibility-driven loading)
   */
  const fetchEventByIdQuery = (eventId: MaybeRefOrGetter<string>, options?: { enabled?: MaybeRefOrGetter<boolean> }) =>
    useQuery<EventSearchResult, Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const lId = groupsStore.currentGroup?.locationId
        return queryKeys.detail(gId, lId, toValue(eventId))
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = groupsStore.currentGroup?.locationId
        const id = toValue(eventId)

        if (!id) throw new Error('Event ID is required')
        if (!gId) throw new Error('Group ID is required')
        if (!lId) throw new Error('Location ID is required')

        return service.getEventById(id, gId, lId)
      },
      enabled: computed(() => {
        if (!import.meta.client) return false
        if (!toValue(eventId)) return false
        if (!toValue(groupId) || !groupsStore.currentGroup?.locationId) return false
        return toValue(options?.enabled) !== false
      }),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    })

  // ===== EVENTS LIST QUERY =====
  const eventsListQuery = () =>
    useQuery<EventMentionData[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        return queryKeys.list(gId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId

        if (!gId || !locationId) {
          throw new Error('Group ID and Location ID are required for fetching events')
        }

        // Set date range: 12 months ahead and 2 months back
        const now = new Date()
        const startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1)
        const endDate = new Date(now.getFullYear(), now.getMonth() + 12, 0)

        const events = await service.searchTaggableEvents({
          search: '', // Empty search to get all events
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupId: gId,
          locationId,
        })

        // Transform to mention format
        return events.slice(0, 10).map(event => {
          const occurrenceStartTime = event.isRecurring ? event.nextOccurrence || event.startTime : event.startTime
          // Save timestamp in milliseconds to reduce stored string size in db
          const eventDate = new Date(occurrenceStartTime).getTime()

          return {
            id: event._id,
            label: event.title,
            type: 'events-mention' as const,
            timestamp: eventDate,
            startTime: occurrenceStartTime,
            endTime: event.endTime,
            nextOccurrence: event.nextOccurrence,
            isRecurring: event.isRecurring,
            eventCoverUrl: event.eventCoverUrl,
            description: event.description,
          }
        })
      },
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      enabled: computed(() => !!toValue(groupId)),
      refetchOnWindowFocus: false,
    })
  // ===== CALENDAR EVENTS QUERY =====
  const calendarEventsQuery = (startDate: MaybeRefOrGetter<Date>, endDate: MaybeRefOrGetter<Date>) =>
    useQuery<EventSearchResult[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const start = toValue(startDate)
        const end = toValue(endDate)
        return queryKeys.calendar(gId, start?.toISOString(), end?.toISOString())
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        const start = toValue(startDate)
        const end = toValue(endDate)

        if (!gId || !locationId) {
          throw new Error('Group ID and Location ID are required for fetching calendar events')
        }

        const events = await service.getCalendarEvents({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          groupId: gId,
          locationId,
        })

        // Transform to mention format
        return events
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => !!toValue(groupId)),
      refetchOnWindowFocus: false,
    })

  // ===== PAST AND UPCOMING EVENTS QUERY =====
  // Fetches both past and upcoming events grouped by category
  // Returns: { upcomingEvents: EventSearchResult[], pastEvents: EventSearchResult[] }
  const pastAndUpcomingEventsQuery = () =>
    useQuery<{ upcomingEvents: EventSearchResult[]; pastEvents: EventSearchResult[] }, Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        return queryKeys.incoming(gId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        return service.fetchPastAndUpcomingEvents({ groupId: gId, locationId })
      },
      enabled: computed(() => !!toValue(groupId)),
      refetchOnWindowFocus: false,
    })

  const eventCountQuery = (eventId: MaybeRefOrGetter<string>) =>
    useQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const eId = toValue(eventId)
        return queryKeys.count(gId, eId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        const eId = toValue(eventId)

        if (!gId || !locationId || !eId) {
          throw new Error('Group ID, Location ID, and Event ID are required for fetching event count')
        }

        return service.getEventRSVPCount({ groupId: gId, locationId, eventId: eId })
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const gId = toValue(groupId)
        const eId = toValue(eventId)
        const isEnabled = !!gId && !!eId
        return isEnabled
      }),
      refetchOnWindowFocus: true,
    })

  const getAttendeesQuery = (eventId: MaybeRefOrGetter<string>) => {
    return useQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const eId = toValue(eventId)
        console.log('🔑 Query key computed - gId:', gId, 'eId:', eId)
        return queryKeys.attendees(gId, eId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        const eId = toValue(eventId)
        console.log('🚀 queryFn executing - gId:', gId, 'locationId:', locationId, 'eId:', eId)

        if (!gId || !locationId || !eId) {
          console.error('❌ Missing required parameters:', { gId, locationId, eId })
          throw new Error('Group ID, Location ID, and Event ID are required for fetching attendees')
        }

        return service.getEventAttendees({ groupId: gId, locationId, eventId: eId, limit: 20 })
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const gId = toValue(groupId)
        const eId = toValue(eventId)
        const isEnabled = !!gId && !!eId
        console.log('✅ Query enabled check - gId:', gId, 'eId:', eId, 'isEnabled:', isEnabled)
        return isEnabled
      }),
      refetchOnWindowFocus: false,
    })
  }

  // ===== EXPORT ATTENDEES QUERY =====
  const exportAttendeesQuery = (eventId: MaybeRefOrGetter<string>) =>
    useQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const eId = toValue(eventId)
        return queryKeys.exportAttendees(gId, eId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const locationId = groupsStore.currentGroup?.locationId
        const eId = toValue(eventId)

        if (!gId || !locationId || !eId) {
          throw new Error('Group ID, Location ID, and Event ID are required for exporting attendees')
        }

        return service.exportEventAttendees({ groupId: gId, locationId, eventId: eId })
      },
      staleTime: 0, // Always fetch fresh data for exports
      gcTime: 0, // Don't cache export data
      enabled: false, // Manual trigger only - don't auto-fetch
      refetchOnWindowFocus: false,
    })

  // ===== MUTATIONS =====

  const updateRSVPMutation = useMutation({
    mutationFn: (variables: { eventId: string; status: 'ATTENDING' | 'DECLINED' | 'MAYBE'; paymentMeta?: { paymentStatus: string } }) => {
      const gId = toValue(groupId)
      const locationId = groupsStore.currentGroup?.locationId

      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for RSVP')
      }

      return service.updateEventRSVP({
        groupId: gId,
        locationId,
        eventId: variables.eventId,
        status: variables.status,
        paymentMeta: variables.paymentMeta,
      })
    },
    onSuccess: (_response, variables) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Update the cached event-detail query for this event
      // so the inline embed card can switch RSVP -> "You're going" immediately.
      queryClient.setQueriesData<EventSearchResult>(
        {
          predicate: query => {
            const [scope, currentGroupId, keyType, , eventId] = query.queryKey
            return scope === 'events' && currentGroupId === gId && keyType === 'detail' && eventId === variables.eventId
          },
        },
        old => {
          if (!old) return old
          const nextHasRegistered = variables.status === 'ATTENDING'
          const nextRsvpMeta = nextHasRegistered
            ? {
                _id: old.rsvpMeta?._id ?? 'optimistic-rsvp',
                ...(old.rsvpMeta?.paymentMeta ? { paymentMeta: old.rsvpMeta.paymentMeta } : {}),
                hasRegistered: true,
              }
            : old.rsvpMeta
              ? {
                  ...old.rsvpMeta,
                  hasRegistered: false,
                }
              : undefined

          return {
            ...old,
            rsvpMeta: nextRsvpMeta,
          }
        }
      )

      // Refresh count/list/calendar derived views using stable prefixes.
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar(gId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.incoming(gId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.count(gId) })

      // AUTO welcome checklist: BE completes RSVP_EVENT after attending.
      if (variables.status === 'ATTENDING') {
        const locationId = groupsStore.currentGroup?.locationId
        refreshMemberOnboardingAfterActivity(queryClient, locationId, gId, ['RSVP_EVENT'])
      }
    },
  })

  const createEventMutation = useMutation({
    mutationFn: (variables: EventFormDataType) => {
      const gId = toValue(groupId)
      const locationId = groupsStore.currentGroup?.locationId
      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for creating event')
      }
      return service.createEvent({
        groupId: gId,
        locationId: locationId,
        formData: {
          ...variables,
          locationId: locationId,
          groupId: gId,
        },
      })
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      // Invalidate all event queries to refresh the calendar
      queryClient.invalidateQueries({ queryKey: queryKeys.incoming(gId) })
    },
  })

  const updateEventMutation = useMutation({
    mutationFn: (variables: EventFormDataType) => {
      const gId = toValue(groupId)
      const locationId = groupsStore.currentGroup?.locationId
      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for updating event')
      }
      if (!variables._id) {
        throw new Error('Event ID is required for updating event')
      }
      return service.updateEvent({
        groupId: gId,
        locationId: locationId,
        eventId: variables._id,
        formData: {
          ...variables,
          locationId: locationId,
          groupId: gId,
        },
      })
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      // Invalidate all event queries to refresh the calendar
      queryClient.invalidateQueries({ queryKey: queryKeys.incoming(gId) })
    },
  })

  const deleteEventMutation = useMutation({
    mutationFn: ({ eventId, deleteParams }: { eventId: string; deleteParams?: any }) => {
      const gId = toValue(groupId)
      const locationId = toValue(groupsStore.currentGroup?.locationId)
      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for deleting event')
      }
      return service.deleteEvent({
        groupId: gId,
        locationId: locationId,
        eventId,
        deleteParams: deleteParams || {},
      })
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      // Invalidate all event queries to refresh the calendar
      queryClient.invalidateQueries({ queryKey: queryKeys.incoming(gId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar(gId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.list(gId) })
    },
  })

  const createMeetingMutation = useMutation({
    mutationFn: (variables: { title: string; type: MeetingType }) => {
      const gId = toValue(groupId)
      const locationId = groupsStore.currentGroup?.locationId
      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for creating meeting')
      }
      return service.createMeeting({ groupId: gId, locationId, ...variables })
    },
  })

  const addEventToCalendarMutation = useMutation({
    mutationFn: (variables: { eventId: string; calendar: string }) => {
      const gId = toValue(groupId)
      const locationId = groupsStore.currentGroup?.locationId
      if (!gId || !locationId) {
        throw new Error('Group ID and Location ID are required for adding event to calendar')
      }
      if (!variables.eventId) {
        throw new Error('Event ID is required for adding event to calendar')
      }
      if (!variables.calendar) {
        throw new Error('Calendar type is required for adding event to calendar')
      }
      return service.addEventToCalendar({
        groupId: gId,
        locationId: locationId,
        eventId: variables.eventId,
        calendar: variables.calendar,
      })
    },
    onSuccess: (data, variables) => {
      // Return calendar URL or data for user to download/open
      console.log('Event added to calendar:', variables.calendar, data)
    },
  })

  // ===== PUBLIC API =====
  return {
    // Query key factory for external use
    queryKeys,

    // Query factories
    eventsListQuery,
    searchEventsQuery,
    calendarEventsQuery,
    pastAndUpcomingEventsQuery,
    eventCountQuery,
    getAttendeesQuery,
    exportAttendeesQuery,
    fetchEventByIdQuery,

    // Mutations
    updateRSVPMutation,
    createEventMutation,
    deleteEventMutation,
    updateEventMutation,
    createMeetingMutation,
    addEventToCalendarMutation,
  }
}
